<template>
  <div>
    <Card class="h-full">
      <CardHeader>
        <CardTitle>Latest cashback events</CardTitle>
        <CardDescription>Most recent transactions across your programs</CardDescription>
        <CardAction>
          <Button variant="outline" size="sm" @click="goToLedger">
            View all
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <SharedDataTable
          :headers="headers"
          :items="events"
          :loading="loading"
          :error="error"
          @retry="fetchEvents()"
          :default-rows-per-page="10"
          :rows-items="[10, 25, 50]"
        >
          <template #item-offer="ev">
            <div class="flex items-center gap-2">
              <img
                v-if="retailerLogo(ev)"
                :src="retailerLogo(ev)"
                :alt="retailerName(ev)"
                class="w-6 h-6 rounded border border-border object-contain shrink-0 bg-white"
              />
              <span class="text-sm font-medium">{{ retailerName(ev) }}</span>
            </div>
          </template>

          <template #item-date="ev">
            <span class="text-sm text-muted-foreground">{{ formatDate(ev.createdOn) }}</span>
          </template>

          <template #item-status="ev">
            <span class="flex items-center gap-1.5 text-sm text-muted-foreground">
              <component
                :is="statusIcon(ev.status)"
                :class="[
                  'size-3.5',
                  statusKind(ev.status) === 'done' ? 'text-green-500 dark:text-green-400' : '',
                  statusKind(ev.status) === 'failed' ? 'text-red-500 dark:text-red-400' : '',
                  statusKind(ev.status) === 'progress' ? 'animate-spin' : '',
                ]"
              />
              {{ formatStatus(ev.status) }}
            </span>
          </template>

          <template #item-transaction="ev">
            <!-- Narrow truncation: this card is half-width and now carries five columns,
                 so a wider id pushes the date column out of view. Full value on hover/copy. -->
            <CopyId
              v-if="transactionId(ev)"
              :value="transactionId(ev)"
              :max-width="80"
            />
            <span v-else class="text-xs text-muted-foreground">—</span>
          </template>

          <template #item-cashback="ev">
            <div class="text-sm font-medium tabular-nums">
              {{ cashback(ev) }}
            </div>
          </template>
        </SharedDataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script>
import {
  Badge,
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CopyId,
} from '@boogi/ui'
import { CircleCheck, CircleX, Loader } from 'lucide-vue-next'
import { DataTable as SharedDataTable } from '@boogi/ui'

export default {
  name: 'DataTable',
  components: {
    SharedDataTable,
    Badge,
    Button,
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    CopyId,
  },
  data() {
    return {
      loading: true,
      error: false,
      events: [],
      headers: [
        { text: 'Offer', value: 'offer' },
        { text: 'Status', value: 'status' },
        { text: 'Cashback', value: 'cashback' },
        { text: 'Transaction ID', value: 'transaction' },
        { text: 'Date', value: 'date' },
      ],
    }
  },
  created() {
    this.fetchEvents()
  },
  methods: {
    transactionId(ev) {
      return ev.internalTransactionId || ev.transactionId || ''
    },
    retailerName(ev) {
      return (ev.programOffer && ev.programOffer.retailer && ev.programOffer.retailer.retailerName) || 'Unknown retailer'
    },
    retailerLogo(ev) {
      return (ev.programOffer && ev.programOffer.retailer && ev.programOffer.retailer.logo) || ''
    },
    formatDate(ts) {
      if (!ts) return '—'
      return new Date(ts).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    },
    formatStatus(status) {
      if (!status) return '—'
      return status
        .toLowerCase()
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
    },
    statusKind(status) {
      if (['PAID', 'RECEIVED', 'VERIFIED'].includes(status)) return 'done'
      if (['DECLINED', 'DISQUALIFIED', 'REFUNDED'].includes(status)) return 'failed'
      return 'progress'
    },
    statusIcon(status) {
      const kind = this.statusKind(status)
      if (kind === 'done') return CircleCheck
      if (kind === 'failed') return CircleX
      return Loader
    },
    cashback(ev) {
      const payout = ev.payoutData
      if (!payout || payout.endUserCashbackReceivable == null) return '—'
      const currency = payout.currency === 'GBP' ? '£' : `${payout.currency || ''} `
      const amount = Number(payout.endUserCashbackReceivable).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
      return `${currency}${amount}`
    },
    goToLedger() {
      this.$router.push('/cashback-program/ledger')
    },
    async fetchEvents() {
      this.loading = true
      this.error = false
      try {
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL
        const token = localStorage.getItem('token')
        const statuses = ['CREATED', 'BEING_VERIFIED', 'VERIFIED', 'RECEIVED', 'PAID', 'DECLINED', 'REFUNDED']
        const res = await this.$apiFetch(
          `${apiUrl}/private/api/client/cashback-ledger?page=0&size=100&status=${statuses}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )
        if (res.ok) {
          const obj = await res.json()
          this.events = obj.content || []
        } else {
          this.error = true
        }
      } catch (e) {
        this.events = []
        this.error = true
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
